import { zh } from '../i18n';
import { CLAUDE_MD_MAX_BYTES } from '../lib/limits';

/**
 * Structure lint for the one file the UI can write (SPEC R25).
 *
 * It warns, never blocks: `CLAUDE.md` is the agent's working brief, and a user
 * who deliberately restructures it should not be stopped by a heading list.
 * The size check is the exception — over 128 KB the server rejects the PUT
 * (`413 TOO_LARGE`), so saying so before the round-trip is just honest.
 */

export interface LintIssue {
  readonly id: string;
  readonly level: 'warn' | 'error';
  readonly message: string;
}

const PLACEHOLDER = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;

export function byteLength(content: string): number {
  return new TextEncoder().encode(content).length;
}

export function lintClaudeMd(content: string): LintIssue[] {
  const issues: LintIssue[] = [];

  if (content.trim().length === 0) {
    issues.push({ id: 'empty', level: 'error', message: zh.claudeMd.lintEmpty });
    return issues;
  }

  for (const heading of zh.claudeMd.requiredHeadings) {
    if (!content.includes(heading)) {
      issues.push({
        id: `heading:${heading}`,
        level: 'warn',
        message: zh.claudeMd.lintMissingHeading(heading),
      });
    }
  }

  const seen = new Set<string>();
  for (const match of content.matchAll(PLACEHOLDER)) {
    const name = match[1];
    if (name === undefined || seen.has(name)) continue;
    seen.add(name);
    issues.push({
      id: `placeholder:${name}`,
      level: 'warn',
      message: zh.claudeMd.lintPlaceholder(name),
    });
  }

  const bytes = byteLength(content);
  if (bytes > CLAUDE_MD_MAX_BYTES) {
    issues.push({
      id: 'size',
      level: 'error',
      message: zh.claudeMd.tooLarge(Math.round(CLAUDE_MD_MAX_BYTES / 1024)),
    });
  } else if (bytes > CLAUDE_MD_MAX_BYTES * 0.9) {
    issues.push({
      id: 'size-near',
      level: 'warn',
      message: zh.claudeMd.lintTooLarge(Math.round(bytes / 1024)),
    });
  }

  return issues;
}
